<template>
    <div class="aging-inventory-page-wrapper">
        <layout-secondary v-if="!hasFailedRequests"
                          v-show="!showMobileModal"
                          :page-model="pageModel"
                          :title-text="$t('agingInventory.list.title')"
                          back-route="/available-inventories"
        >
            <div class="filters-container">
                <flex-row>
                    <div class="col col--no-grow">
                        <select-filter
                            id="aging-inventory-filter-type"
                            name="filters.filterTypes"
                            :column="filters.filterTypes"
                            :searchable="false"
                            option-label="label"
                            value-field="value"
                            track-by="value"
                            @filter="filter"
                        >
                            <template
                                #singleLabel="{ option }"
                            >
                                {{ option.label }}
                            </template>
                        </select-filter>
                    </div>
                    <div class="col col--no-grow">
                        <select-filter
                            id="aging-inventory-date-range"
                            name="filters.dateRange"
                            :column="filters.dateRange"
                            :searchable="false"
                            option-label="label"
                            value-field="value"
                            track-by="value"
                            @filter="filter"
                        >
                            <template
                                #singleLabel="{ option }"
                            >
                                {{ option.label }}
                            </template>
                        </select-filter>
                    </div>
                    <div class="col col--no-grow export-button">
                        <export-button :url="exportUrl"
                                       :text="$t('agingInventory.labels.downloadGridData')"
                        />
                    </div>
                </flex-row>
                <flex-row v-if="messageWarning">
                    <div class="col col--no-grow">
                        <p class="messageWarning">
                            {{ messageWarning() }}
                        </p>
                    </div>
                </flex-row>
            </div>

            <base-table v-if="$mq !== 'mobile' || !pageModel.hasMobileView"
                        fixed-header
                        invert-colors
                        offset-sides="lg"
                        :row-uniq-id="pageModel.uniqueField"
                        :columns="pageModel.columns"
                        :rows="pageModel.table.items"
                        :actions="pageModel.actions"
                        :loading="pageModel.table.loading"
            >
                <p slot="noData">
                    {{ $t('agingInventory.list.noResults') }}
                </p>
            </base-table>

            <base-pagination
                v-if="hasPages && $mq !== 'mobile'"
                slot="pagination"
                :current-page="pageModel.table.page"
                :results="pageModel.table.total"
                :items-per-page="pageModel.table.pageSize"
                :page-route="currentPageRoute"
                class="list-page__footer"
                @paging="paging"
            />
        </layout-secondary>
        <layout-secondary v-else>
            <failed-to-load @retry="reloadComponent" />
        </layout-secondary>
    </div>
</template>

<script>
import { $t } from '@/translations';
import constants from '@/core/constants';
import { Serializers } from '@/core/table/tableStateUrl';
import agingItemsApi from '@/services/resources/agingItemsApi';
import formatters from '@/core/table/tableFormatters';
import ExportButton from '@/components/buttons/ExportButton';
import { LayoutSecondary } from '@/components/layout';
import { BaseFilterModel, SelectFilterModel, DateRangeFilterModel } from '@/core/table/filterModels';
import {
    BaseTable,
    BasePagination,
    SelectFilter,
} from '@/components/table';
import { ListPageModel } from '@/components/pages';
import FailedToLoad from '@/components/FailedToLoad';

const filterSerializer = {
    expiryDateFrom: Serializers.date,
    expiryDateTo: Serializers.date,
};

export default {
    components: {
        FailedToLoad,
        BaseTable,
        BasePagination,
        LayoutSecondary,
        SelectFilter,
        ExportButton,
    },
    props: {
    },
    data() {
        const skuColumn = {
            name: $t('agingInventory.labels.sku'),
            field: 'itemId',
            minWidth: '100px',
        };

        const minDate = new Date();
        minDate.setDate(minDate.getDate() - constants.domain.maxOrderSearchDays);
        const expirationDateColumn = {
            name: $t('agingInventory.labels.expirationDate'),
            field: 'expirationDate',
            format: formatters.date('short', false),
            minWidth: '14%',
            filter: new DateRangeFilterModel(null, 'expirationDateFrom', 'expirationDateTo', {
                max: new Date(),
                min: minDate,
            }),
        };

        const warehouseColumn = {
            name: $t('agingInventory.labels.warehouse'),
            field: 'warehouse',
            minWidth: '16%',
        };

        const lotNumberColumn = {
            name: $t('agingInventory.labels.lotNumber'),
            field: 'lotNumber',
            minWidth: '16%',
        };

        const filters = {
            searchQuery: {
                field: 'searchQuery',
                placeholder: $t('agingInventory.filters.searchPlaceholder'),
                tag: $t('general.filters.search'),
                filter: new BaseFilterModel(''),
            },
            filterTypes: expirationDateColumn,
            dateRange: expirationDateColumn,
        };

        const columns = [
            skuColumn,
            {
                name: $t('agingInventory.labels.description'),
                field: 'description',
                minWidth: '16%',
            },
            expirationDateColumn,
            warehouseColumn,
            lotNumberColumn,
            {
                name: $t('agingInventory.labels.quantity'),
                field: 'quantity',
                minWidth: '14%',
            },
        ];

        const pageModel = new ListPageModel(agingItemsApi, {
            router: this.$router,
            serializers: filterSerializer,
        }, {
            uniqueField: 'id',
            filters,
            columns,
            hasMobileView: true,
        }, {
            defaultPageSize: 10,
            itemsField: 'expiredItems',
        });

        return {
            pageModel,
            filters,
            baseColumns: columns,
            baseWarehouseColumns: [],
            mobileModalOpened: false,
            openPreviousModal: false,
            modalData: {
                modalName: null,
                expiredItems: null,
            },
            failedWarehouse: false,
            warehouses: [],
            isFilterLoaded: false,
        };
    },
    computed: {
        hasPages() {
            return this.pageModel.hasPages();
        },
        hasFailedRequests() {
            return this.pageModel.hasFailedRequests();
        },
        isTabletSize() {
            return this.$mq === 'tablet' || this.$mq === 'mobile';
        },
        showMobileModal() {
            return this.mobileModalOpened && this.isTabletSize;
        },
        currentPageRoute() {
            return this.$route.path;
        },
        exportUrl() {
            const isExpiredWindowView = this.pageModel.filters.filterTypes.filter.value;
            const dateRange = this.pageModel.filters.dateRange.filter.value;
            return agingItemsApi.getExportPath(isExpiredWindowView, dateRange);
        },
    },
    watch: {
        $mq(value) {
            this.pageModel.updateMobileView(value === 'mobile');
        },
        hasFailedRequests(value) {
            if (value) {
                const error = this.pageModel.getError();

                this.$toast.error(error);
            }
        },
    },
    async mounted() {
        await this.loadFilters();
        await this.pageModel.restore();
    },
    methods: {
        async loadFilters() {
            const filterTypes = [{ filter: 'Product Expiration Date', value: false }, { filter: 'Product Expiration Window', value: true }];
            await this.loadFilterTypeFilter(filterTypes);
            const dateRange = [{ filter: 'Next 30 Days', value: 30 }, { filter: 'Next 60 Days', value: 60 }, { filter: 'Next 90 Days', value: 90 }, { filter: 'Next 180 Days', value: 180 }];
            await this.loadDateRangeFilter(dateRange);
        },
        async loadFilterTypeFilter(filterTypes) {
            this.pageModel.filters.filterTypes = {
                name: $t('agingInventory.labels.filterTypes'),
                field: 'IsExpiredWindowView',
                filter: new SelectFilterModel(
                    null,
                    filterTypes ? filterTypes.map(x => ({
                        label: x.filter,
                        value: x.value,
                    })) : [],
                ),
            };
        },
        async loadDateRangeFilter(dateRange) {
            this.pageModel.filters.dateRange = {
                name: $t('agingInventory.labels.dateRange'),
                field: 'dateRange',
                filter: new SelectFilterModel(
                    null,
                    dateRange ? dateRange.map(x => ({
                        label: x.filter,
                        value: x.value,
                    })) : [],
                ),
            };
        },
        filter(field, value) {
            const isExpiredWindowView = this.isExpiredWindowViewValue();
            const dateRange = this.dateRangeValue();
            if (isExpiredWindowView !== null && dateRange !== null) {
                this.pageModel.filter(field, value);
            } else {
                this.pageModel.updateFilters(field, value);
            }
        },
        refreshTable() {
            return this.pageModel.refresh();
        },
        reloadComponent() {
            this.loadFilters();
            this.refreshTable();
        },
        closeModal() {
            this.mobileModalOpened = false;
            this.openPreviousModal = false;
        },
        paging(data) {
            this.pageModel.paging(data);
        },
        isExpiredWindowViewValue() {
            const pIsExpiredWindowView = this.pageModel.filters.filterTypes.filter.value == null ? null
                : this.pageModel.filters.filterTypes.filter.value;
            return pIsExpiredWindowView;
        },
        dateRangeValue() {
            const pDateRange = this.pageModel.filters.dateRange.filter.value == null ? null : this.pageModel.filters.dateRange.filter.value;
            return pDateRange;
        },
        messageWarning() {
            if (this.isExpiredWindowViewValue() == null && this.dateRangeValue() != null) {
                return $t('agingInventory.labels.FilterTypeWarning');
            }
            if (this.isExpiredWindowViewValue() != null && this.dateRangeValue() == null) {
                return $t('agingInventory.labels.DateRangeWarning');
            }
            return '';
        },
    },
};
</script>

<style scoped lang="scss">
.aging-inventory-page-wrapper {
    height: 100%;

    .export-button {
        margin-left: auto;
    }

    .search-container {
        position: absolute;
        top: 75px;
        right: 20px;
        width: 360px;

        ::v-deep .search {
            ::placeholder {
                color: $color-gray-400;
            }

            &__input,
            &__icon {
                color: $color-gray-200;
            }
        }

        @media screen and (max-width: $media-mobile-nav) {
            top: 25px;
        }
    }

    .filters-container {
        margin: 0 -50px;
        padding: 20px;
        background-color: $color-teal-920;

        ::v-deep .vc-popover-content-wrapper {
            z-index: 51;
        }

        .manage-edi-button {
            margin-left: 20px;
        }

        .header-buttons {
            margin-right: 0;
            margin-left: auto;

            .edit-allocation-button {
                margin-right: 20px;
            }
        }
    }

    .messageWarning {
        margin-left: 20px;
        color: #ff5733;
    }
}

.mobile-view {
    margin: 0 (-$page-padding-sides);
}
</style>
